import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { TIER_ACCESS, normaliseTier } from '@/lib/tierConfig';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) throw new Error('useRole must be used within RoleProvider');
  return context;
};

// ── Nav sections ─────────────────────────────────────────────────────────────
export const NAV_SECTIONS = [
  {
    id: 'dashboard',
    sectionLabel: 'Dashboard',
    items: [
      { path: '/dashboard',         label: 'Overview',          permission: null },
      { path: '/savings',           label: 'Your Savings',      permission: null },
      { path: '/smart-actions',     label: 'Smart Actions',     permission: null },
      { path: '/customer-insights', label: 'Customer Insights', permission: null },
    ],
  },
  {
    id: 'billing',
    sectionLabel: null,
    items: [
      { path: '/pos', label: 'Billing', permission: null },
    ],
  },
  {
    id: 'stock',
    sectionLabel: null,
    items: [{ path: '/stock', label: 'Stock', permission: null }],
  },
  {
    id: 'purchase',
    sectionLabel: null,
    items: [
      { path: '/purchases', label: 'Purchase', permission: null },
    ],
  },
  {
    id: 'khata',
    sectionLabel: null,
    items: [
      { path: '/credit-customers', label: 'Khata', permission: null },
    ],
  },
  {
    id: 'reports',
    sectionLabel: 'Reports',
    items: [
      { path: '/reports',    label: 'Advanced Reports', permission: null },
      { path: '/accounting', label: 'Accounting & GST', permission: null },
    ],
  },
  {
    id: 'appendix',
    sectionLabel: null,
    items: [{ path: '/appendix', label: 'Appendix', permission: null }],
  },
];

export const EMPLOYEE_ALLOWED_PATHS = ['/savings', '/smart-actions', '/stock', '/customer-insights', '/credit', '/credit-customers', '/appendix', '/stock-master', '/pos', '/purchases'];

// ── Provider ─────────────────────────────────────────────────────────────────
export const RoleProvider = ({ children }) => {
  const { user, storeProfile } = useAuth();

  const role = user?.role || 'customer';
  const rawTier = normaliseTier(storeProfile?.membership_tier);
  // Mirror the JWT hook's tier cap: cashier → always starter; manager → never enterprise
  const tier = role === 'cashier' ? 'starter'
             : role === 'manager' ? (rawTier === 'enterprise' ? 'growth' : rawTier)
             : rawTier;
  const access = TIER_ACCESS[tier];

  // All logged-in users are treated as "customer" — platform_admin is separate
  const isOwner        = () => true;
  const isAdmin        = () => true;
  const isStoreManager = () => false;
  const isEmployee     = () => false;

  const hasPermission = () => true; // all nav items visible to all customers (tier gates handled separately)

  const canAccessPage           = (path) => access.pages.includes(path);
  const isPageLocked            = (path) => !access.pages.includes(path);
  const canUseSmartActionsInStock = ()   => access.smartActionsInStock;

  const value = {
    role,
    tier,
    isOwner,
    isAdmin,
    isStoreManager,
    isEmployee,
    hasPermission,
    canAccessPage,
    isPageLocked,
    canUseSmartActionsInStock,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
